import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  RadioGroup,
  RadioGroupItem,
} from '@folio/components';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import { useSuspenseQuery } from '@tanstack/react-query';
import { viewSpecificControllerListPolicyAssetsQueryOptions } from 'korint-sdk';
import { StarIcon } from 'lucide-react';

const availableTiers = (isLuxe?: boolean) => ({
  THREE_STARS: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex gap-1">
          {Array.from({ length: 3 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
        </div>
        {isLuxe && <span className="text-xs ml-1 self-center">Luxe</span>}
      </div>
    ),
  },
  FOUR_STARS: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex gap-1">
          {Array.from({ length: 4 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
        </div>
        {isLuxe && <span className="text-xs ml-1 self-center">Luxe</span>}
      </div>
    ),
  },
  FIVE_STARS: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex gap-1">
          {Array.from({ length: 5 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
        </div>
        {isLuxe && <span className="text-xs ml-1 self-center">Luxe</span>}
      </div>
    ),
  },
  FIVE_STARS_ECO: {
    name: (
      <div className="flex items-center mb-2 flex-wrap">
        <div className="mr-2">Garantie</div>
        <div className="flex items-center">
          {Array.from({ length: 5 }).map((_, starIndex) => (
            <StarIcon
              // biome-ignore lint/suspicious/noArrayIndexKey: is ok
              key={`star-${starIndex}`}
              className="h-4 w-4 fill-yellow-400 text-yellow-400"
            />
          ))}
          <span className="text-xs ml-1 self-center">éco</span>
        </div>
      </div>
    ),
  },
});

const availablePeriodDuration = (amount: number, unit: string): string | undefined => {
  if (unit !== 'MONTH') return undefined;

  switch (amount) {
    case 6:
      return 'SIX';
    case 12:
      return 'TWELVE';
    case 24:
      return 'TWENTY_FOUR';
    default:
      return undefined;
  }
};

export function QuoteCard() {
  const { policy, workingBranch } = usePolicyOnDashboard();
  const { data: asset } = useSuspenseQuery({
    ...viewSpecificControllerListPolicyAssetsQueryOptions(policy.id, {
      branchId: workingBranch?.id,
    }),
    select: assets => {
      const startedAssets = assets.data.filter(asset => asset.status === 'ASSET_STARTED');

      if (startedAssets.length === 0) {
        const assetsSortedByStartedAtDesc = assets.data.sort((asset1, asset2) => {
          if (!asset1.startedAt || !asset2.startedAt) {
            return 0;
          }
          return asset1.startedAt < asset2?.startedAt ? 1 : -1;
        });

        return assetsSortedByStartedAtDesc[0];
      }

      return startedAssets[0];
    },
  });

  const isLuxe = (policy.customFields.plan as string | undefined)?.includes('LUXE');
  const availableTiersResult = availableTiers(isLuxe);

  const tierKey = asset?.quote?.options.tier as keyof typeof availableTiersResult;

  const selectedDuration = policy?.periodDuration
    ? availablePeriodDuration(policy.periodDuration.amount, policy.periodDuration.unit)
    : undefined;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Choix des options</CardTitle>
      </CardHeader>
      <CardContent>
        <table>
          <tbody>
            <tr>
              <td className="pr-12 font-semibold text-sm">Formule</td>
              <td className="p-3 text-left">
                {tierKey && availableTiers(isLuxe)[tierKey]
                  ? availableTiers(isLuxe)[tierKey].name
                  : 'Indisponible'}
              </td>
            </tr>
            <tr>
              <td className="pr-12 font-semibold text-sm">Durée de la garantie</td>
              <td className="p-3 text-left">
                <RadioGroup className="flex gap-8" value={selectedDuration}>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="SIX" disabled={selectedDuration !== 'SIX'} />
                    <label htmlFor="three-stars">6 mois</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="TWELVE" disabled={selectedDuration !== 'TWELVE'} />
                    <label htmlFor="four-stars">12 mois</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem
                      value="TWENTY_FOUR"
                      disabled={selectedDuration !== 'TWENTY_FOUR'}
                    />
                    <label htmlFor="five-stars">24 mois</label>
                  </div>
                </RadioGroup>
              </td>
            </tr>
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
